/**
 * Protocol-Process Mapping Component
 * 
 * Component for linking protocols to processes
 * Part of Policy Framework & EU Diseases Implementation
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useProtocolProcessMappings, useProtocolProcessMappingMutations } from '../../hooks/useProtocolProcessMapping';
import { useProtocols } from '../../hooks/useProtocols';
import { useProcesses } from '../../hooks/useProcesses';
import { usePolicyFrameworks } from '../../hooks/usePolicyFrameworks';
import { usePolicyDocuments, useRequirementsByPolicy } from '../../hooks/useQmsPolicy';
import { useNotifications } from '../NotificationSystem';
import Modal from '../ui/Modal';
import './ProtocolProcessMapping.css';

const ProtocolProcessMapping = ({ organizationId, userId, userName }) => {
  const { t } = useTranslation('ipc');
  const { mappings, loading, error, refresh } = useProtocolProcessMappings(organizationId);
  const { createMapping, updateMapping, deleteMapping, loading: mutating } = useProtocolProcessMappingMutations();
  const { protocols, loading: protocolsLoading } = useProtocols({ status: 'active' });
  const { processes, loading: processesLoading } = useProcesses(organizationId);
  const { frameworks } = usePolicyFrameworks(organizationId);
  const { documents: policyDocuments } = usePolicyDocuments({ status: 'active' });
  const { notify } = useNotifications();

  const [showForm, setShowForm] = useState(false);
  const [editingMapping, setEditingMapping] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [mappingToDelete, setMappingToDelete] = useState(null);
  const [filterProtocol, setFilterProtocol] = useState('all');
  const [filterProcess, setFilterProcess] = useState('all');
  const [filterFramework, setFilterFramework] = useState('all');

  const [formData, setFormData] = useState({
    protocolId: '',
    processId: '',
    policyFrameworkId: '',
    policyDocumentId: '',
    policyDocumentName: '',
    requirementId: '',
    requirementCode: '',
    requirementText: '',
    processStep: '',
    linkType: 'implements',
    implementationNotes: '',
    complianceRequired: false,
    auditFrequency: 'monthly'
  });

  const { requirements: mappingRequirements, loading: requirementsLoading } = useRequirementsByPolicy(
    formData.policyDocumentId || null
  );

  const filteredMappings = mappings.filter(mapping => {
    if (filterProtocol !== 'all' && mapping.protocolId !== filterProtocol) return false;
    if (filterProcess !== 'all' && mapping.processId !== filterProcess) return false;
    if (filterFramework !== 'all' && mapping.policyFrameworkId !== filterFramework) return false;
    return true;
  });

  const handleCreate = () => {
    setEditingMapping(null);
    setFormData({
      protocolId: '',
      processId: '',
      policyFrameworkId: '',
      policyDocumentId: '',
      policyDocumentName: '',
      requirementId: '',
      requirementCode: '',
      requirementText: '',
      processStep: '',
      linkType: 'implements',
      implementationNotes: '',
      complianceRequired: false,
      auditFrequency: 'monthly'
    });
    setShowForm(true);
  };

  const handleEdit = (mapping) => {
    setEditingMapping(mapping);
    setFormData({
      protocolId: mapping.protocolId || '',
      processId: mapping.processId || '',
      policyFrameworkId: mapping.policyFrameworkId || '',
      policyDocumentId: mapping.policyDocumentId || '',
      policyDocumentName: mapping.policyDocumentName || '',
      requirementId: mapping.requirementId || '',
      requirementCode: mapping.requirementCode || '',
      requirementText: mapping.requirementText || '',
      processStep: mapping.processStep || '',
      linkType: mapping.linkType || 'implements',
      implementationNotes: mapping.implementationNotes || '',
      complianceRequired: mapping.complianceRequired || false,
      auditFrequency: mapping.auditFrequency || 'monthly'
    });
    setShowForm(true);
  };

  const handleDelete = (mappingId) => {
    setMappingToDelete(mappingId);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    try {
      await deleteMapping(mappingToDelete);
      notify.success('Mapping deleted successfully');
      refresh();
      setShowDeleteConfirm(false);
      setMappingToDelete(null);
    } catch (err) {
      notify.error(err.message || 'Failed to delete mapping');
    }
  };

  const handleSave = async () => {
    try {
      if (!formData.protocolId || !formData.processId) {
        notify.error('Protocol and Process are required');
        return;
      }

      const selectedProtocol = protocols.find(p => p.id === formData.protocolId);
      const selectedProcess = processes.find(p => p.id === formData.processId);
      const selectedFramework = formData.policyFrameworkId
        ? frameworks.find(f => f.id === formData.policyFrameworkId)
        : null;
      const selectedDoc = formData.policyDocumentId
        ? policyDocuments.find(d => d.id === formData.policyDocumentId)
        : null;

      const saveData = {
        organizationId,
        protocolId: formData.protocolId,
        protocolName: selectedProtocol?.name || '',
        protocolCode: selectedProtocol?.code || '',
        processId: formData.processId,
        processName: selectedProcess?.processName || selectedProcess?.name || '',
        processCode: selectedProcess?.processCode || '',
        policyFrameworkId: formData.policyFrameworkId || null,
        policyFrameworkName: selectedFramework?.name || null,
        policyDocumentId: formData.policyDocumentId || null,
        policyDocumentName: selectedDoc ? (selectedDoc.title || selectedDoc.code || '') : null,
        requirementId: formData.requirementId || null,
        requirementCode: formData.requirementCode || null,
        requirementText: formData.requirementText || null,
        processStep: formData.processStep ? parseInt(formData.processStep) : null,
        linkType: formData.linkType,
        implementationNotes: formData.implementationNotes || '',
        complianceRequired: formData.complianceRequired || false,
        auditFrequency: formData.auditFrequency || 'monthly'
      };

      if (editingMapping) {
        await updateMapping(editingMapping.id, saveData, userId, userName);
        notify.success(t('mappingUpdated'));
      } else {
        await createMapping(saveData, userId, userName);
        notify.success(t('mappingCreated'));
      }

      setShowForm(false);
      setEditingMapping(null);
      refresh();
    } catch (err) {
      notify.error(err.message || t('failedToSaveMapping'));
    }
  };

  const getProtocolName = (protocolId) => {
    const protocol = protocols.find(p => p.id === protocolId);
    return protocol?.name || protocolId;
  };

  const getProcessName = (processId) => {
    const process = processes.find(p => p.id === processId);
    return process?.processName || process?.name || processId;
  };

  const getFrameworkName = (frameworkId) => {
    if (!frameworkId) return 'N/A';
    const framework = frameworks.find(f => f.id === frameworkId);
    return framework?.name || frameworkId;
  };

  const getLinkTypeBadge = (linkType) => {
    const styles = {
      implements: { bg: '#c6f6d5', color: '#22543d' },
      supports: { bg: '#bee3f8', color: '#2c5282' },
      references: { bg: '#fef5e7', color: '#744210' }
    };
    const style = styles[linkType] || styles.implements;
    return (
      <span 
        className="link-type-badge"
        style={{ backgroundColor: style.bg, color: style.color }}
      >
        {linkType}
      </span>
    );
  };

  if (loading || protocolsLoading || processesLoading) {
    return (
      <div className="protocol-process-mapping">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading mappings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="protocol-process-mapping">
      <div className="manager-header">
        <div>
          <h2>{t('protocolProcessMapping')}</h2>
          <p className="subtitle">{t('linkProtocolsToProcesses')}</p>
        </div>
        <button 
          className="btn btn-primary"
          onClick={handleCreate}
          disabled={!organizationId}
        >
          {t('newMapping')}
        </button>
        {!organizationId && (
          <div className="error-message" style={{ marginTop: '16px' }}>
            ⚠️ {t('noOrganizationPolicy')}
          </div>
        )}
      </div>

      {error && (
        <div className="error-message">
          ❌ {error}
        </div>
      )}

      {/* Filters */}
      <div className="filter-section">
        <div className="filter-group">
          <label>{t('filterByProtocol')}</label>
          <select
            value={filterProtocol}
            onChange={(e) => setFilterProtocol(e.target.value)}
            className="filter-select"
          >
            <option value="all">{t('allProtocols')}</option>
            {protocols.map(protocol => (
              <option key={protocol.id} value={protocol.id}>
                {protocol.name}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>{t('filterByProcess')}</label>
          <select
            value={filterProcess}
            onChange={(e) => setFilterProcess(e.target.value)}
            className="filter-select"
          >
            <option value="all">{t('allProcesses')}</option>
            {processes.map(process => (
              <option key={process.id} value={process.id}>
                {process.processName || process.name}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>{t('filterByFramework')}</label>
          <select
            value={filterFramework}
            onChange={(e) => setFilterFramework(e.target.value)}
            className="filter-select"
          >
            <option value="all">{t('allFrameworks')}</option>
            {frameworks.map(framework => (
              <option key={framework.id} value={framework.id}>
                {framework.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Mappings List */}
      <div className="mappings-list">
        {filteredMappings.length === 0 ? (
          <div className="empty-state">
            <p>{t('noMappingsFound')}</p>
          </div>
        ) : (
          <table className="mappings-table">
            <thead>
              <tr>
                <th>{t('protocol')}</th>
                <th>{t('process')}</th>
                <th>{t('framework')}</th>
                <th>{t('policyDocument')}</th>
                <th>{t('requirement')}</th>
                <th>{t('linkType')}</th>
                <th>{t('processStep')}</th>
                <th>{t('complianceRequired')}</th>
                <th>{t('actions')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredMappings.map(mapping => (
                <tr key={mapping.id}>
                  <td>
                    <strong>{mapping.protocolName || getProtocolName(mapping.protocolId)}</strong>
                    {mapping.protocolCode && (
                      <>
                        <br /><small className="code-text">{mapping.protocolCode}</small>
                      </>
                    )}
                  </td>
                  <td>
                    <strong>{mapping.processName || getProcessName(mapping.processId)}</strong>
                    {mapping.processCode && (
                      <>
                        <br /><small className="code-text">{mapping.processCode}</small>
                      </>
                    )}
                  </td>
                  <td>{getFrameworkName(mapping.policyFrameworkId)}</td>
                  <td>{mapping.policyDocumentName || '—'}</td>
                  <td title={mapping.requirementText || ''}>
                    {mapping.requirementCode ? (
                      <span>{mapping.requirementCode}{mapping.requirementText ? ` — ${mapping.requirementText.slice(0, 40)}${mapping.requirementText.length > 40 ? '…' : ''}` : ''}</span>
                    ) : '—'}
                  </td>
                  <td>{getLinkTypeBadge(mapping.linkType)}</td>
                  <td>{mapping.processStep || 'N/A'}</td>
                  <td>
                    {mapping.complianceRequired ? (
                      <span className="badge badge-warning">Yes</span>
                    ) : (
                      <span className="badge badge-secondary">No</span>
                    )}
                  </td>
                  <td className="actions">
                    <button 
                      className="btn btn-sm btn-secondary"
                      onClick={() => handleEdit(mapping)}
                    >
                      Edit
                    </button>
                    <button 
                      className="btn btn-sm btn-danger"
                      onClick={() => handleDelete(mapping.id)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={showForm}
        onClose={() => {
          setShowForm(false);
          setEditingMapping(null);
        }}
        title={editingMapping ? t('editMapping') : t('createNewMapping')}
        size="large"
      >
        <div className="mapping-form">
          <div className="form-row">
            <div className="form-group">
              <label>
                {t('protocol')} <span className="required">*</span>
              </label>
              <select
                value={formData.protocolId}
                onChange={(e) => setFormData({ ...formData, protocolId: e.target.value })}
                style={{ width: '100%', padding: '8px' }}
                required
              >
                <option value="">{t('selectProtocol')}</option>
                {protocols.map(protocol => (
                  <option key={protocol.id} value={protocol.id}>
                    {protocol.name} {protocol.code ? `(${protocol.code})` : ''}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>
                Process <span className="required">*</span>
              </label>
              <select
                value={formData.processId}
                onChange={(e) => setFormData({ ...formData, processId: e.target.value })}
                style={{ width: '100%', padding: '8px' }}
                required
              >
                <option value="">{t('nomenclatures:options.selectProcess')}</option>
                {processes.map(process => (
                  <option key={process.id} value={process.id}>
                    {process.processName || process.name} {process.processCode ? `(${process.processCode})` : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>{t('policyFramework')}</label>
              <select
                value={formData.policyFrameworkId}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    policyFrameworkId: e.target.value,
                    policyDocumentId: '',
                    policyDocumentName: '',
                    requirementId: '',
                    requirementCode: '',
                    requirementText: ''
                  })
                }
                style={{ width: '100%', padding: '8px' }}
              >
                <option value="">{t('selectFrameworkOptional')}</option>
                {frameworks.map(framework => (
                  <option key={framework.id} value={framework.id}>
                    {framework.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>{t('policyDocument')}</label>
              <select
                value={formData.policyDocumentId}
                onChange={(e) => {
                  const id = e.target.value || '';
                  const doc = policyDocuments.find(d => d.id === id);
                  setFormData({
                    ...formData,
                    policyDocumentId: id,
                    policyDocumentName: doc ? (doc.title || doc.code || '') : '',
                    requirementId: '',
                    requirementCode: '',
                    requirementText: ''
                  });
                }}
                style={{ width: '100%', padding: '8px' }}
              >
                <option value="">— {t('none')} —</option>
                {(formData.policyFrameworkId
                  ? policyDocuments.filter(d => d.policyFrameworkId === formData.policyFrameworkId)
                  : policyDocuments
                ).map(doc => (
                  <option key={doc.id} value={doc.id}>
                    {doc.title || doc.code || doc.id}
                  </option>
                ))}
              </select>
              <small className="form-hint">{t('policyDocumentMappingHint')}</small>
            </div>
          </div>

          {formData.policyDocumentId && (
            <div className="form-group">
              <label>{t('requirement')}</label>
              <select
                value={formData.requirementId}
                onChange={(e) => {
                  const id = e.target.value || '';
                  const req = mappingRequirements.find(r => r.id === id);
                  setFormData({
                    ...formData,
                    requirementId: id,
                    requirementCode: req ? req.code : '',
                    requirementText: req ? req.requirementText : ''
                  });
                }}
                style={{ width: '100%', padding: '8px' }}
                disabled={requirementsLoading}
              >
                <option value="">— {t('none')} —</option>
                {mappingRequirements.map(req => (
                  <option key={req.id} value={req.id}>
                    {req.code} — {req.requirementText ? req.requirementText.slice(0, 60) + (req.requirementText.length > 60 ? '…' : '') : ''}
                  </option>
                ))}
              </select>
              <small className="form-hint">{t('requirementMappingHint')}</small>
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label>{t('linkType')}</label>
              <select
                value={formData.linkType}
                onChange={(e) => setFormData({ ...formData, linkType: e.target.value })}
                style={{ width: '100%', padding: '8px' }}
              >
                <option value="implements">{t('implements')}</option>
                <option value="supports">{t('supports')}</option>
                <option value="references">{t('references')}</option>
              </select>
            </div>
            <div className="form-group">
              <label>{t('processStep')}</label>
              <input
                type="number"
                value={formData.processStep}
                onChange={(e) => setFormData({ ...formData, processStep: e.target.value })}
                placeholder={t('placeholderProcessStep')}
                style={{ width: '100%', padding: '8px' }}
              />
              <small style={{ color: '#718096', fontSize: '12px' }}>
                {t('whichStepImplements')}
              </small>
            </div>

            <div className="form-group">
              <label>{t('auditFrequency')}</label>
              <select
                value={formData.auditFrequency}
                onChange={(e) => setFormData({ ...formData, auditFrequency: e.target.value })}
                style={{ width: '100%', padding: '8px' }}
              >
                <option value="daily">{t('daily')}</option>
                <option value="weekly">{t('weekly')}</option>
                <option value="monthly">{t('monthly')}</option>
                <option value="quarterly">{t('quarterly')}</option>
                <option value="annually">{t('annually')}</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>{t('implementationNotes')}</label>
            <textarea
              value={formData.implementationNotes}
              onChange={(e) => setFormData({ ...formData, implementationNotes: e.target.value })}
              rows={3}
              placeholder={t('placeholderImplementationNotes')}
              style={{ width: '100%', padding: '8px' }}
            />
          </div>

          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={formData.complianceRequired}
                onChange={(e) => setFormData({ ...formData, complianceRequired: e.target.checked })}
              />
              {t('complianceRequired')}
            </label>
          </div>

          <div className="form-actions">
            <button
              className="btn btn-secondary"
              onClick={() => {
                setShowForm(false);
                setEditingMapping(null);
              }}
            >
              {t('cancel')}
            </button>
            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={mutating || !formData.protocolId || !formData.processId}
            >
              {editingMapping ? t('saveChanges') : t('createMapping')}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        title={t('confirmDelete')}
        size="small"
      >
        <p>{t('confirmDeleteMapping')}</p>
        <div className="form-actions">
          <button className="btn btn-secondary" onClick={() => setShowDeleteConfirm(false)}>
            {t('cancel')}
          </button>
          <button className="btn btn-danger" onClick={confirmDelete} disabled={mutating}>
            {t('delete')}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default ProtocolProcessMapping;

